import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  CircleMinus,
  RefreshCcw,
  ShieldCheck,
} from "lucide-react";

import {
  controlPassportWorkspace,
  evaluateControlPassport,
} from "@/lib/api";
import { useData } from "@/lib/app-view";
import {
  checkpointRequirements,
  requirementExplanation,
  toneForPassport,
  toneLabel,
  type ControlTone,
  type PassportCheckpoint,
  type PassportRequirement,
  type PassportSnapshot,
} from "@/lib/control-passport-presentation";
import { Button } from "@/components/ui/button";
import { displayDate } from "@/lib/utils";

type Capabilities = {
  approve?: boolean;
  procure?: boolean;
  receive?: boolean;
  finance?: boolean;
  manage?: boolean;
};

type Props = {
  organisationId: string;
  checkpoint: PassportCheckpoint;
  requestNo?: string | null;
  caseId?: string | null;
  viewerRole: string;
  capabilities?: Capabilities;
  onNavigate?: (view: string, recordId?: string) => void;
  compact?: boolean;
};

const toneStyle: Record<ControlTone, string> = {
  green: "border-[var(--success-border)] bg-[var(--success-soft)] text-[var(--success)]",
  amber: "border-[var(--warning-border)] bg-[var(--warning-soft)] text-[var(--warning)]",
  red: "border-[var(--error-border)] bg-[var(--error-soft)] text-[var(--error)]",
  grey: "border-[var(--border)] bg-[var(--surface-secondary)] text-[var(--text-secondary)]",
};

function ToneIcon({ tone }: { tone: ControlTone }) {
  const props = { className: "size-5 shrink-0", "aria-hidden": true } as const;
  if (tone === "green") return <CheckCircle2 {...props} />;
  if (tone === "amber") return <AlertTriangle {...props} />;
  if (tone === "red") return <Ban {...props} />;
  return <CircleMinus {...props} />;
}

function canOpen(view: string, capabilities: Capabilities) {
  if (view === "approvals") return capabilities.approve || capabilities.manage;
  if (["rfqs", "orders", "vendors"].includes(view)) return capabilities.procure || capabilities.manage;
  if (view === "receipts") return capabilities.receive || capabilities.manage;
  if (view === "invoices") return capabilities.finance || capabilities.manage;
  if (view === "money") return capabilities.finance || capabilities.approve || capabilities.manage;
  return true;
}

export function ControlPassportPanel({
  organisationId,
  checkpoint,
  requestNo,
  caseId,
  viewerRole,
  capabilities = {},
  onNavigate,
  compact = false,
}: Props) {
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const { data, error, loading } = useData(
    () => controlPassportWorkspace(organisationId, caseId || undefined),
    [organisationId, caseId, refresh],
  );
  const row = useMemo(
    () =>
      data?.cases.find(
        (item) =>
          (caseId && item.id === caseId) ||
          (requestNo && item.request?.request_no === requestNo),
      ),
    [caseId, data, requestNo],
  );
  const snapshot = row?.snapshots?.[0] as PassportSnapshot | undefined;
  const tone = toneForPassport(snapshot, row?.control_origin);
  const requirements = checkpointRequirements(snapshot, checkpoint);
  const policyVersionId = snapshot?.payload?.policy_version_id;
  const policy = data?.policies
    .flatMap((item) =>
      (item.versions || []).map((version: Record<string, any>) => ({
        ...version,
        policyName: item.policy?.name,
      })),
    )
    .find((version) => version.id === policyVersionId);

  async function reevaluate() {
    if (!row?.id) return;
    setBusy(true);
    setActionError("");
    try {
      await evaluateControlPassport(organisationId, row.id, checkpoint);
      setRefresh((value) => value + 1);
    } catch (reason) {
      setActionError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className={`rounded-2xl border border-[var(--border)] bg-[var(--surface)] ${compact ? "p-4" : "p-5"}`}
      aria-labelledby={`control-passport-${checkpoint}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="rounded-xl bg-[var(--surface-secondary)] p-2 text-[var(--text-primary)]">
            <ShieldCheck className="size-5" aria-hidden="true" />
          </span>
          <div>
            <p className="eyebrow">CONTROL PASSPORT · {checkpoint.replaceAll("_", " ")}</p>
            <h2 id={`control-passport-${checkpoint}`} className="mt-1 text-lg font-semibold">
              {row?.control_id || requestNo || "Control evidence"}
            </h2>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Evaluated from the retained policy version and server-side evidence.
            </p>
          </div>
        </div>
        <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold ${toneStyle[tone]}`} role="status" aria-live="polite">
          <ToneIcon tone={tone} />
          {toneLabel(tone)}
        </div>
      </div>

      {loading && (
        <p className="mt-4 text-sm text-[var(--text-secondary)]" role="status">
          Loading tenant-scoped control evidence…
        </p>
      )}
      {error && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--error-border)] bg-[var(--error-soft)] p-3 text-sm text-[var(--error)]" role="alert">
          <span>Control evidence is temporarily unavailable: {error}</span>
          <Button size="sm" variant="outline" onClick={() => setRefresh((value) => value + 1)}>
            <RefreshCcw className="size-4" aria-hidden="true" />
            Retry
          </Button>
        </div>
      )}
      {!loading && !error && !row && (
        <div className="mt-4 rounded-xl border border-[var(--border)] bg-[var(--surface-secondary)] p-4 text-sm">
          <strong>Control Passport not available yet.</strong>
          <p className="mt-1 text-[var(--text-secondary)]">
            A tenant-scoped procurement case must exist before evidence can be evaluated. No compliance result is inferred.
          </p>
        </div>
      )}
      {!loading && !error && row && (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <PassportFact label="Completeness" value={snapshot ? `${snapshot.completeness_percent || 0}%` : "Not evaluated"} />
            <PassportFact label="Policy" value={policy ? `${policy.policyName || "Organisation policy"} · v${policy.version}` : "Baseline evidence set"} />
            <PassportFact label="Effective from" value={policy?.effective_from ? displayDate(policy.effective_from) : "Not recorded"} />
            <PassportFact label="Viewer" value={viewerRole.replaceAll("_", " ")} />
          </div>
          {requirements.length ? (
            <div className="mt-4 grid gap-3 lg:grid-cols-2">
              {requirements.map((requirement: PassportRequirement) => {
                const explanation = requirementExplanation(requirement, {
                  name: policy?.policyName,
                  version: policy?.version,
                  effectiveFrom: policy?.effective_from ? displayDate(policy.effective_from) : null,
                });
                const actionAvailable =
                  onNavigate && canOpen(explanation.view, capabilities) && explanation.tone !== "green";
                return (
                  <article key={requirement.requirement_code} className={`rounded-xl border p-4 ${toneStyle[explanation.tone]}`}>
                    <div className="flex items-start gap-2">
                      <ToneIcon tone={explanation.tone} />
                      <div>
                        <h3 className="font-semibold">{requirement.label || requirement.requirement_code}</h3>
                        <p className="mt-1 text-sm">{toneLabel(explanation.tone)}</p>
                      </div>
                    </div>
                    <dl className="mt-3 grid gap-2 text-sm text-[var(--text-primary)]">
                      <PassportDetail term="Why" value={explanation.why} />
                      <PassportDetail term="Policy" value={explanation.policy} />
                      <PassportDetail term="Next" value={explanation.next} />
                      <PassportDetail term="Resolver" value={explanation.resolver} />
                    </dl>
                    {actionAvailable ? (
                      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                        <Button className="min-h-11 w-full sm:w-auto" size="sm" onClick={() => onNavigate(explanation.view)}>
                          {explanation.action}
                        </Button>
                        {["rfq", "quotation", "comparison", "award"].includes(checkpoint) &&
                          (capabilities.procure || capabilities.approve || capabilities.manage) && (
                            <Button
                              className="min-h-11 w-full sm:w-auto"
                              size="sm"
                              variant="outline"
                              onClick={() => onNavigate("internal-controls", row?.id)}
                            >
                              Request waiver
                            </Button>
                          )}
                      </div>
                    ) : explanation.tone !== "green" ? (
                      <p className="mt-3 text-xs font-medium">
                        Waiting on {explanation.resolver}; this role cannot perform the controlled action.
                      </p>
                    ) : null}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="mt-4 rounded-xl border border-[var(--border)] bg-[var(--surface-secondary)] p-4 text-sm text-[var(--text-secondary)]">
              This checkpoint has no applicable evaluated requirement in the latest snapshot.
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border)] pt-4">
            <p className="text-xs text-[var(--text-secondary)]">
              {snapshot
                ? `Snapshot v${snapshot.version} · ${snapshot.generated_at ? displayDate(snapshot.generated_at) : "date unavailable"} · immutable hash retained`
                : "No snapshot has been generated for this case."}
            </p>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => void reevaluate()}>
              <RefreshCcw className="size-4" aria-hidden="true" />
              {busy ? "Evaluating…" : "Evaluate current evidence"}
            </Button>
          </div>
          {actionError && <p className="mt-3 text-sm text-[var(--error)]" role="alert">{actionError}</p>}
        </>
      )}
    </section>
  );
}

function PassportFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-secondary)] p-3">
      <span className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">{label}</span>
      <strong className="mt-1 block text-sm capitalize text-[var(--text-primary)]">{value}</strong>
    </div>
  );
}

function PassportDetail({ term, value }: { term: string; value: string }) {
  return (
    <div>
      <dt className="font-semibold">{term}</dt>
      <dd className="mt-0.5 opacity-90">{value}</dd>
    </div>
  );
}
